import _ from "lodash";
import { useLazyQuery } from "@apollo/client";
import { MY_TIME_GET_DASHBOARD_LIST } from "my-time/graphql/queries";
import { periodFormat } from "my-time/utils/period-format";

const parseJDTimestamp = (timestamp: string): Date | null => {
  if (!timestamp) return null;
  const year = timestamp.slice(0, 4);
  const month = timestamp.slice(4, 6);
  const day = timestamp.slice(6, 8);
  const hours = timestamp.slice(8, 10);
  const minutes = timestamp.slice(10, 12);
  const dateString = `${year}-${month}-${day} ${hours}:${minutes}`;
  return new Date(dateString);
};

export interface TableData {
  sortField?: string;
  sortDirection?: string;
  page?: number;
  pageSize?: number;
  searchField?: string;
  searchText?: string;
}
export interface UseMyTimeDashboardListOptions {
  roleFilters?: Record<string, any>;
  tableData?: TableData;
  role?: string;
  date?: string;
}

export const useMyTimeDashboardList = (
  options: UseMyTimeDashboardListOptions = {}
) => {
  const { roleFilters = {}, tableData = {}, role, date } = options;

  const filters = { ...roleFilters };
  delete filters.__typename;

  const variables: {
    roleFilters: typeof filters;
    sortField?: string;
    sortDirection?: string;
    searchField?: string;
    searchText?: string;
    role?: string;
    page?: number;
    pageSize?: number;
  } = {
    roleFilters: filters,
    sortField: tableData.sortField,
    sortDirection: tableData.sortDirection,
    role,
    page: tableData.page,
    pageSize: tableData.pageSize,
  };

  if (tableData.searchField && tableData.searchText) {
    variables.searchField = tableData.searchField;
    variables.searchText = tableData.searchText;
  }

  const [rawLoadQuery, { data, loading, error }] = useLazyQuery(
    MY_TIME_GET_DASHBOARD_LIST,
    {
      fetchPolicy: "cache-and-network",
    }
  );

  const loadQuery = () => {
    rawLoadQuery({ variables });
  };

  interface DashboardListItem {
    approvedAt?: string | null;
    pipedName?: string;
    recordingPeriod?: string;
    [key: string]: any;
  }

  const list: DashboardListItem[] = data?.dashboardList || [];
  const uniqueList = _.uniqBy(list, "pipedName");

  const formattedPeriodDate = periodFormat(date);

  const parsedList = uniqueList.map((item) => {
    const lastModificationDateObj = parseJDTimestamp(
      item?.lastModificationDate || ""
    );
    const lastModificationDate = lastModificationDateObj
      ? lastModificationDateObj.getTime()
      : 0;

    let isApprovedOK = false;
    let isAcknowledgedOK = false;
    let isAuthorizedOK = false;

    const isAcknowledged = item.acknowledgedAt ? true : false;
    const acknowledgedAt = new Date(item.acknowledgedAt).getTime();


    if (
      isAcknowledged &&
      acknowledgedAt > lastModificationDate &&
      item.recordingPeriod === formattedPeriodDate
    ) {
      isAcknowledgedOK = true;
    }

    const isApproved = item.approvedAt ? true : false;
    const approvedAt = item.approvedAt
      ? new Date(item.approvedAt).getTime()
      : 0;

    if (
      isApproved &&
      approvedAt > lastModificationDate &&
      item.recordingPeriod === formattedPeriodDate
    ) {
      isApprovedOK = true;
    }

    const isAuthorized = item.authorizedAt ? true : false;
    const authorizedAt = item.authorizedAt
      ? new Date(item.authorizedAt).getTime()
      : 0;

    if (
      isAuthorized &&
      authorizedAt > lastModificationDate &&
      item.recordingPeriod === formattedPeriodDate
    ) {
      isAuthorizedOK = true;
    }

    return {
      ...item,
      _id: item._id ?? item.contractCode,
      isAcknowledged: isAcknowledgedOK,
      isApproved: isApprovedOK,
      isAuthorized: isAuthorizedOK,
    };
  });

  return {
    loadQuery,
    data: parsedList,
    loading,
    error,
  };
};

export default useMyTimeDashboardList;
